/**
 * Renders announcement content with minimal Markdown-style link support.
 *
 * Supports:
 *   - Markdown links: [label](https://example.com) -> <a href=... target=_blank>
 *   - Bare URLs:      https://example.com          -> auto-linkified
 *   - Line breaks:    \n -> <br>
 *
 * Safety: the input is HTML-escaped first, and only http(s) / mailto
 * URLs are accepted, so `javascript:` and other unsafe schemes cannot
 * slip through. The output is intended to be used with
 * `dangerouslySetInnerHTML`.
 */

// Basic HTML escape for user-supplied text.
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// Only allow http(s) and mailto: URLs. Everything else is rejected
// (and rendered as the original literal text).
function isSafeUrl(url) {
  return /^(https?:\/\/|mailto:)/i.test(url)
}

// Build an <a> tag with the standard new-tab attributes. `label` and
// `url` come in already HTML-escaped.
function anchor(url, label) {
  return `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`
}

/**
 * Escape a raw announcement `content` string and turn markdown-style
 * links + bare URLs into safe anchor tags. Returns an HTML string
 * suitable for `dangerouslySetInnerHTML={{ __html: ... }}`.
 */
export function renderAnnouncementContent(text) {
  if (!text) return ''

  // Escape everything first so any pre-existing HTML/JS is neutralised.
  let html = escapeHtml(text)

  // Placeholders keep already-processed anchors safe from the bare-URL
  // pass that follows.
  const placeholders = []
  const stash = (tag) => {
    const key = ` LINK${placeholders.length} `
    placeholders.push(tag)
    return key
  }

  // 1) Markdown-style [label](url). After escapeHtml, `"` becomes
  // `&quot;` — so match `[^)]+` for the URL and require it to not
  // contain whitespace.
  html = html.replace(/\[([^\]]+)\]\((\S+?)\)/g, (match, label, url) => {
    if (!isSafeUrl(url)) return match
    return stash(anchor(url, label))
  })

  // 2) Bare URLs (http/https). Trim trailing punctuation that's
  // almost certainly not part of the URL.
  html = html.replace(/(https?:\/\/[^\s<]+)/g, (match) => {
    let url = match
    let trailing = ''
    const trailMatch = url.match(/[.,;:!?)]+$/)
    if (trailMatch) {
      trailing = trailMatch[0]
      url = url.slice(0, -trailing.length)
    }
    if (!isSafeUrl(url)) return match
    return stash(anchor(url, url)) + trailing
  })

  // Line breaks -> <br>
  html = html.replace(/\n/g, '<br>')

  // Re-insert the stashed anchor tags.
  html = html.replace(/ LINK(\d+) /g, (_, i) => placeholders[Number(i)])

  return html
}

/**
 * Normalises a user-typed URL. If it lacks a scheme but looks like
 * a domain (e.g. `example.com/foo`), prepend `https://`. mailto: and
 * existing schemes pass through unchanged.
 */
export function normalizeUrl(input) {
  const url = String(input || '').trim()
  if (!url) return ''
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return url // already has a scheme
  // Looks like a domain? (has a dot, no spaces)
  if (/^[^\s]+\.[^\s]+$/.test(url)) return `https://${url}`
  return url
}
